import {isFeatureEnabled} from 'feature-flags';
import {Parameter} from 'platform/components';

import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {useGetLastActiveServiceCaseQuery} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {useGetVehicleHeaderV2, usePermissions} from '@dms/shared';

import {Nullish, buildArray, isNotNilOrEmpty, composePath, useNavigate} from 'shared/web';

import {useGetVehicleHeaderV3} from './useGetVehicleHeaderV3';

export function useGetVehicleHeaderInformationParameters(
  isSaleVehicle: boolean,
  isServiceVehicle: boolean,
  vehicle: GetVehicleApiResponseV2 | Nullish,
  hasComments: boolean,
  additionalNote: string | Nullish
) {
  const navigate = useNavigate();
  const [canReadServiceCase] = usePermissions({permissionKeys: ['serviceCaseRead']});
  const canReadWorkshop = canReadServiceCase;

  const navigateToInsights = () => {
    if (!vehicle?.id) {
      return;
    }
    navigate(composePath(vehiclesRoutes.insights, {params: {id: vehicle.id}}));
  };

  const {data: lastActiveServiceCase} = useGetLastActiveServiceCaseQuery(
    {vehicleId: vehicle?.id || ''},
    {skip: !vehicle?.id || !canReadWorkshop}
  );

  const newVehicleHeader = useGetVehicleHeaderV2({
    serviceCaseNumber: lastActiveServiceCase?.number,
    publicId: vehicle?.publicId,
    vin: vehicle?.vin,
    registrationPlate: vehicle?.registrationPlate,
    firstRegistrationOn: vehicle?.firstRegistrationOn,
    warrantyDate: vehicle?.warrantyDate,
    technicalInspectionValidUntil: vehicle?.technicalInspectionValidUntil,
    engineCode: vehicle?.engineCode,
    isVehicleDetail: true,
    hasComments,
    additionalNote,
    onIconClick: navigateToInsights,
  });

  const vehicleHeaderV3 = useGetVehicleHeaderV3({
    publicId: vehicle?.publicId,
    externalId: vehicle?.externalId,
    vin: vehicle?.vin,
    registrationPlate: vehicle?.registrationPlate,
    firstRegistrationOn: vehicle?.firstRegistrationOn,
    warrantyDate: vehicle?.warrantyDate,
    technicalInspectionValidUntil: vehicle?.technicalInspectionValidUntil,
    engineCode: vehicle?.engineCode,
    isServiceVehicle,
    hasComments,
    additionalNote,
    onIconClick: navigateToInsights,
  });

  if (isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3)) {
    return vehicleHeaderV3;
  }

  if (canReadServiceCase && !isSaleVehicle) {
    return newVehicleHeader;
  }

  return buildArray<Parameter>()
    .when(isNotNilOrEmpty(additionalNote), {
      icon: 'action/description',
      title: null,
      variant: 'secondary',
      tooltip: additionalNote,
      onClick: navigateToInsights,
    })
    .when(hasComments, {
      icon: 'communication/comment',
      title: null,
      variant: 'secondary',
      hideSeparator: true,
      onClick: navigateToInsights,
    })
    .when(vehicle?.publicId, {
      title: vehicle?.publicId ?? '',
      tooltip: i18n.t('entity.vehicle.labels.publicId'),
    })
    .when(vehicle?.externalId, {
      title: vehicle?.externalId ?? '',
      tooltip: i18n.t('entity.vehicle.labels.externalId'),
    })
    .add({
      title: vehicle?.vin || i18n.t('entity.vehicle.notifications.noVIN'),
      tooltip: i18n.t('entity.vehicle.labels.vin'),
    })
    .add({
      title: vehicle?.registrationPlate || i18n.t('entity.vehicle.notifications.noLicencePlate'),
      tooltip: i18n.t('entity.vehicle.labels.licensePlate'),
    });
}
